import React, { useEffect, useState } from 'react';
import PatientPortalRouter from '../PatientPortalRouter/PatientPortalRouter';
import { useContext } from 'react';
import { UserEngagementContext } from '../../../utils/userEngagementContext';
import { SourceContext } from '../../../utils/sourceContext';
import InactivityPopUp from '../InactivityPopUp/InactivityPopUp';
import usePopUp from '../../../hooks/usePopUp';

import styles from './footer.module.css';
import logo from '../../../assets/logo.png';
import { Link } from 'react-router-dom';

function Footer() {

    const [isInactivityPopUpOpen, openInactivityPopUp, closeInactivityPopUp] = usePopUp(false);
    const [patientRouterOpen, setPatientRouterOpen] = useState(false);
    const { latestLeadTimestamp, hasTidioInteracted } = useContext(UserEngagementContext);
    const { source } = useContext(SourceContext);

    
    // Derived state on each render (this ensures that the component accesses the latest values)
    const shouldBlockBySource = source === "email";
    const shouldBlockByTidio = hasTidioInteracted;

    // Show inactivity popUp to the user only once per 3 days from last successful response
    const shouldBlockByRecentLead = (() => {
        const todaysDate = localStorage.getItem("todaysDate");
        if (!todaysDate || latestLeadTimestamp === null) return false;

        const responseDate = new Date(latestLeadTimestamp);
        const todayDate = new Date(todaysDate);
        const differenceInDays = (todayDate - responseDate) / (1000 * 3600 * 24);

        return differenceInDays <= 3;
    })();

    const shouldShowInactivity = !shouldBlockBySource && !shouldBlockByTidio && !shouldBlockByRecentLead;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {

        if (!shouldShowInactivity) return;

        const timeout = setTimeout(() => {
            if (isInactivityPopUpOpen) return;
            openInactivityPopUp();
        }, 240000); // 4 minutes

        return () => clearTimeout(timeout);

    }, [shouldShowInactivity]);

    return (
       <>
        <PatientPortalRouter open={patientRouterOpen} onClose={() => setPatientRouterOpen(false)} />
        <footer className={styles.footer}>

            <div className={styles.body}>

                {/* Brand */}
                <div className={styles.brand}>
                    <img className={styles.logo} src={logo} alt='TB2 Health' onClick={() => window.scrollTo(0, 0)} />
                    <p className={styles.tagline}>Personalized weight-loss programs backed by licensed clinicians.</p>
                    <a
                        className={styles.legit}
                        href="https://www.legitscript.com/websites/?checker_keywords=tb2.health"
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Verify LegitScript Approval for www.tb2.health"
                    >
                        <img src="https://static.legitscript.com/seals/18502077.png" alt="Verify LegitScript Approval for www.tb2.health" />
                    </a>
                </div>

                {/* Contact */}
                <div className={styles.contact}>
                    <div className={styles.colLabel}>Get in Touch</div>
                    <div className={styles.contactRow}>
                        <div className={styles.contactIcon}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.15 1.22a2 2 0 012-2.18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 6.91a16 16 0 006.16 6.16l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"></path></svg>
                        </div>
                        <div className={styles.contactInfo}>
                            <span className={styles.contactLabel}>Phone</span>
                            <span className={styles.contactVal}><a href='tel:8445703201'>844.570.3201</a></span>
                        </div>
                    </div>
                    <div className={styles.contactRow}>
                        <div className={styles.contactIcon}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"></path></svg>
                        </div>
                        <div className={styles.contactInfo}>
                            <span className={styles.contactLabel}>SMS</span>
                            <span className={styles.contactVal}><a href='sms:14407974172'>440.797.4172</a></span>
                        </div>
                    </div>
                    <div className={styles.contactRow}>
                        <div className={styles.contactIcon}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                        </div>
                        <div className={styles.contactInfo}>
                            <span className={styles.contactLabel}>Email</span>
                            <span className={styles.contactVal}><a href='mailto:social@tb2.health'>social@tb2.health</a></span>
                        </div>
                    </div>
                </div>

                {/* Nav */}
                <nav className={styles.nav}>
                    <div className={styles.navGroup}>
                        <div className={styles.colLabel}>Programs</div>
                        <Link className={styles.navLink} to='/programs'>Programs</Link>
                        <Link className={styles.navLink} to='/about'>About Us</Link>
                        <Link className={styles.navLink} to='/contact'>Contact Us</Link>
                    </div>
                    <div className={styles.navGroup}>
                        <div className={styles.colLabel}>Resources</div>
                        <Link className={styles.navLink} to='/knowledge-center'>Knowledge Center</Link>
                        <Link className={styles.navLink} to='/articles'>Articles</Link>
                        <Link className={styles.navLink} to='/success-stories'>Success Stories</Link>
                    </div>
                    <div className={styles.navGroup}>
                        <div className={styles.colLabel}>Account</div>
                        <Link className={styles.navLink} to='/affiliates'>Affiliates</Link>
                        <button
                            className={styles.navLink}
                            onClick={() => {process.env.REACT_APP_PATIENT_PORTAL_ENABLED === 'true' ? setPatientRouterOpen(true) : window.open('https://patient.tb2.health', '_blank', 'noopener,noreferrer')}}
                        >
                            Patient Portal
                        </button>
                        <Link className={styles.navLink} to='/billing'>Billing</Link>
                    </div>
                </nav>

            </div>

            <div className={styles.divider}><hr /></div>

            <div className={styles.bottom}>
                <p className={styles.copy}>&copy; 2026 Pillars for Health &middot; 645 Howe Ave #1026, Cuyahoga Falls, OH 44221</p>
                <div className={styles.legal}>
                    <Link className={styles.legalLink} to='/terms' target="_blank" rel="noopener noreferrer">Terms and Conditions</Link>
                    <span className={styles.legalSep}>|</span>
                    <Link className={styles.legalLink} to='/privacy' target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
                    <span className={styles.legalSep}>|</span>
                    <button className={styles.legalLink} onClick={() => window.silktideCookieBannerManager.openPreferencesModal()}>Cookie Preferences</button>
                    <span className={styles.legalSep}>|</span>
                    <Link className={styles.legalLink} to='/cookie-policy' target="_blank" rel="noopener noreferrer">Cookie Policy</Link>
                    <span className={styles.legalSep}>|</span>
                    <button className={styles.legalLink} onClick={() => window.openSiennaWidget?.()}>Accessibility Options</button>
                </div>
            </div>

        </footer>
        <InactivityPopUp isOpen={isInactivityPopUpOpen} onClose={closeInactivityPopUp} />
       </>
    );
}

export default Footer;
